import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import loadable from 'components/utils/lazyImport'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

import type { PropertyMapProps } from '../types'
import type { FullScreenMapModalProps } from './types'

const MODAL_MAP_HEIGHT = '100%'
const MODAL_MAP_WRAPPER_STYLE: React.CSSProperties = { height: MODAL_MAP_HEIGHT }

const InteractivePropertyMap = loadable<PropertyMapProps>(
  () => import(/* webpackChunkName: "InteractivePropertyMap" */ 'components/Common/PropertyMap/InteractivePropertyMap'),
)

const ModalMapBody = styled(ModalBody)`
  min-height: 0;
`

const ModalMapContent = styled(ModalContent)`
  display: flex;
  min-height: 0;
  padding: 0;
`

const ModalMapContainer = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
`

function FullScreenMapModal(props: FullScreenMapModalProps) {
  const {
    className,
    disablePanOnMarkerSelect,
    isSnapBackToOfferEnabled = true,
    focusOnFirstMarker,
    highlightedMarkerId,
    markerOptions,
    markers,
    mapboxAccessTokenUseCase,
    mapLoadingElement,
    mapOptions,
    modalTitle,
    multipleMarkers,
    onDrag,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
  } = props
  const modalElementContext = useModalElementContext()
  const modalMapOptions = useMemo(() => ({
    ...mapOptions,
    fullscreenControl: false,
  }), [mapOptions])
  const handleBackButtonClick = useCallback(() => {
    modalElementContext.resolve()
  }, [modalElementContext])
  const handleInfoWindowNavigateClick = useCallback(() => {
    modalElementContext.resolve()
  }, [modalElementContext])

  // Inject our own onClick handler so map card navigation can close the modal first.
  const renderModalInfoWindow = useMemo(() => {
    if (!renderInfoWindow) {
      return undefined
    }

    return (marker: App.MapMarker, onCloseClick: () => void) => renderInfoWindow(
      marker,
      onCloseClick,
      handleInfoWindowNavigateClick,
    )
  }, [handleInfoWindowNavigateClick, renderInfoWindow])

  return (
    <ModalBase
      size="max"
      height="full"
    >
      <ModalHeader
        title={modalTitle}
        dismissible={false}
        onBackButtonClick={handleBackButtonClick}
      />
      <ModalMapBody>
        <ModalMapContent>
          <ModalMapContainer>
            <InteractivePropertyMap
              className={className}
              disablePanOnMarkerSelect={disablePanOnMarkerSelect}
              enableNearbyMarkerFullscreen={false}
              isSnapBackToOfferEnabled={isSnapBackToOfferEnabled}
              focusOnFirstMarker={focusOnFirstMarker}
              height={MODAL_MAP_HEIGHT}
              highlightedMarkerId={highlightedMarkerId}
              markerOptions={markerOptions}
              markers={markers}
              mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
              mapLoadingElement={mapLoadingElement}
              mapOptions={modalMapOptions}
              multipleMarkers={multipleMarkers}
              showActiveMarkerState={showActiveMarkerState}
              onDrag={onDrag}
              onSelectMarker={onSelectMarker}
              renderInfoWindow={renderModalInfoWindow}
              wrapperStyle={MODAL_MAP_WRAPPER_STYLE}
            />
          </ModalMapContainer>
        </ModalMapContent>
      </ModalMapBody>
    </ModalBase>
  )
}

export default FullScreenMapModal
